<script lang="ts">
    import {onMount} from 'svelte';
    import Button from '$lib/components/common/button/Button.svelte';
    import ClientIntegrationSettings
        from '$lib/components/drawers/client/integrations/ClientIntegrationSettings.svelte';
    import FieldHint from '$lib/components/drawers/client/integrations/FieldHint.svelte';
    import SyncSettings from '$lib/components/drawers/client/integrations/SyncSettings.svelte';
    import {showToast} from '$lib/helpers/toastrHelper';
    import {fetchWithAuth} from '$lib/utils/http/httpUtil';

    export let clientId: number;
  export let clientKey: string | undefined;
  export let organisationId: string | undefined;

  type XeroConfig = {
    defaultInvoiceStatus: string;
    defaultCreditNoteStatus: string;
    xeroBudgetId: string | null;
    financialYearEndMonth: number;
    budgetStartDate: string | null;
    budgetEndDate: string | null;
    profitLossTrackingCategoryId: string | null;
    profitLossTrackingOptionId: string | null;
  };

  type XeroIntegration = {
    aliiClientKey: string;
    aliiEntityKey: string;
    xeroTenantName: string;
    canSync: boolean;
    canInvoicePost: boolean;
    canExpenseClaimPost: boolean;
    syncTaxCodes: boolean;
    syncSuppliers: boolean;
    syncGeneralLedgerCodes: boolean;
    syncBudgets: boolean;
    syncSpends: boolean;
    syncTrackingCategories: boolean;
    timeZoneId: string;
    config: XeroConfig;
    hasEntityConfig: boolean;
    lastSyncRunUtc: string | null;
  };

  type EntityRow = {
    id: number;
    key: string;
    name: string;
    code: string;
    integration: XeroIntegration | null;
  };

  type SyncSetting = {
    key: string;
    label: string;
  };

  type ConfigForm = XeroConfig & {
    timeZoneId: string;
    syncSettings: Record<string, boolean>;
  };

  const customSyncSettings: SyncSetting[] = [{ key: 'syncTrackingCategories', label: 'Tracking Categories' }];
  const postingStatuses = ['DRAFT', 'AUTHORISED'];
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  let entities: EntityRow[] = [];
  let expandedId: number | null = null;
  let forms: Record<number, ConfigForm> = {};
  let isLoading = false;
  let isRefreshing = false;
  let isForcingSync = false;
  let isSaving: Record<number, boolean> = {};

  onMount(load);

  async function load(preserveScroll = false, showLoading = true) {
    if (isLoading || isRefreshing) return;
    if (showLoading) {
      isLoading = true;
    } else {
      isRefreshing = true;
    }
    const scrollY = preserveScroll ? window.scrollY : 0;
    try {
      const response = await fetchWithAuth(`/api/xero-v3/entities/${clientId}`);
      if (response.ok) {
        entities = await response.json();
      } else {
        showToast({ title: 'Error', message: 'Failed to load Xero entities', type: 'error' });
      }
    } catch {
      showToast({ title: 'Error', message: 'Failed to load Xero entities', type: 'error' });
    } finally {
      isLoading = false;
      isRefreshing = false;
      if (preserveScroll) requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: 'instant' }));
    }
  }

  function dateInput(value: string | null) {
    return value ? value.slice(0, 10) : '';
  }

  function openRow(entity: EntityRow) {
    if (expandedId === entity.id) {
      expandedId = null;
      return;
    }

    expandedId = entity.id;
    const integration = entity.integration;
    if (!integration) return;

    forms[entity.id] = {
      timeZoneId: integration.timeZoneId,
      syncSettings: {
        canSync: integration.canSync,
        canInvoicePost: integration.canInvoicePost,
        canExpenseClaimPost: integration.canExpenseClaimPost,
        syncTaxCodes: integration.syncTaxCodes,
        syncSuppliers: integration.syncSuppliers,
        syncGeneralLedgerCodes: integration.syncGeneralLedgerCodes,
        syncBudgets: integration.syncBudgets,
        syncSpends: integration.syncSpends,
        syncTrackingCategories: integration.syncTrackingCategories,
      },
      defaultInvoiceStatus: integration.config.defaultInvoiceStatus,
      defaultCreditNoteStatus: integration.config.defaultCreditNoteStatus,
      xeroBudgetId: integration.config.xeroBudgetId,
      financialYearEndMonth: integration.config.financialYearEndMonth,
      budgetStartDate: dateInput(integration.config.budgetStartDate),
      budgetEndDate: dateInput(integration.config.budgetEndDate),
      profitLossTrackingCategoryId: integration.config.profitLossTrackingCategoryId,
      profitLossTrackingOptionId: integration.config.profitLossTrackingOptionId,
    };
  }

  async function save(entity: EntityRow, isAutoSave = false) {
    const integration = entity.integration;
    const form = forms[entity.id];
    if (!integration || !form || !clientKey) return;

    isSaving = { ...isSaving, [entity.id]: true };
    try {
      const response = await fetchWithAuth(`/api/xero-v3/config/${clientId}/${entity.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId,
          clientKey,
          entityId: entity.id,
          entityKey: entity.key,
          canSync: form.syncSettings.canSync,
          canInvoicePost: form.syncSettings.canInvoicePost,
          canExpenseClaimPost: form.syncSettings.canExpenseClaimPost,
          syncTaxCodes: form.syncSettings.syncTaxCodes,
          syncSuppliers: form.syncSettings.syncSuppliers,
          syncGeneralLedgerCodes: form.syncSettings.syncGeneralLedgerCodes,
          syncBudgets: form.syncSettings.syncBudgets,
          syncSpends: form.syncSettings.syncSpends,
          syncTrackingCategories: form.syncSettings.syncTrackingCategories,
          timeZoneId: form.timeZoneId,
          config: {
            defaultInvoiceStatus: form.defaultInvoiceStatus,
            defaultCreditNoteStatus: form.defaultCreditNoteStatus,
            xeroBudgetId: form.xeroBudgetId || null,
            financialYearEndMonth: Number(form.financialYearEndMonth),
            budgetStartDate: form.budgetStartDate || null,
            budgetEndDate: form.budgetEndDate || null,
            profitLossTrackingCategoryId: form.profitLossTrackingCategoryId || null,
            profitLossTrackingOptionId: form.profitLossTrackingOptionId || null,
          },
        }),
      });

      if (response.ok) {
        showToast({ title: 'Success', message: isAutoSave ? 'Xero sync settings saved' : 'Xero configuration saved', type: 'success' });
        await load(true, false);
      } else {
        showToast({ title: 'Error', message: isAutoSave ? 'Failed to save Xero sync settings' : 'Failed to save Xero configuration', type: 'error' });
      }
    } catch {
      showToast({ title: 'Error', message: isAutoSave ? 'Failed to save Xero sync settings' : 'Failed to save Xero configuration', type: 'error' });
    } finally {
      isSaving = { ...isSaving, [entity.id]: false };
    }
  }

  async function forceSync() {
    isForcingSync = true;
    try {
      const response = await fetchWithAuth(`/api/xero-v3/clients/${clientId}/force-sync`, { method: 'POST' });
      showToast(
        response.ok ? { title: 'Success', message: 'Xero sync has been queued', type: 'success' } : { title: 'Error', message: 'Failed to queue Xero sync', type: 'error' }
      );
    } catch {
      showToast({ title: 'Error', message: 'Failed to queue Xero sync', type: 'error' });
    } finally {
      isForcingSync = false;
    }
  }
</script>

<div class="p-5">
  <h3 class="mb-1 text-xl font-semibold">Xero Integration Setup</h3>
  <p class="mb-4 text-sm text-surface-700">Configure existing Xero integrations. Xero authentication and organisation mapping are managed by the client.</p>

  <ClientIntegrationSettings basePath="/api/xero-v3" {clientId} />
  <div class="mb-4 flex justify-end gap-2">
    <Button label={isForcingSync ? 'Queueing...' : 'Force sync'} onClick={forceSync} disabled={isLoading || isForcingSync} size="small" />
    <Button label={isLoading ? 'Loading...' : 'Refresh'} onClick={load} disabled={isLoading || isForcingSync} colour="secondary" size="small" />
  </div>

  <h4 class="mb-3 text-lg font-semibold text-gray-800">Entities</h4>
  {#if isLoading}
    <p class="text-sm text-gray-500">Loading entities...</p>
  {:else if entities.length === 0}
    <p class="text-sm text-gray-500">No entities found for this client.</p>
  {:else}
    <div class="space-y-2">
      {#each entities as entity (entity.id)}
        <div class="overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
          <button type="button" class="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-100" on:click={() => openRow(entity)}>
            <div class="flex items-center gap-4">
              <span class="text-sm font-medium">{entity.name}</span>
              {#if entity.integration}
                <span class="text-xs text-gray-500">{entity.integration.xeroTenantName}</span>
                <span class="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Configured</span>
                {#if entity.integration.lastSyncRunUtc}
                  <span class="text-xs text-gray-400">Last sync: {entity.integration.lastSyncRunUtc}</span>
                {/if}
              {:else}
                <span class="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-500">Not configured</span>
              {/if}
            </div>
            <span class="ml-2 text-sm text-gray-400">{expandedId === entity.id ? '▲' : '▼'}</span>
          </button>

          {#if expandedId === entity.id}
            <div class="border-t border-gray-200 px-4 pb-4 pt-4">
              {#if !entity.integration}
                <div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  The client must configure and map a Xero organisation to this entity before editing admin settings.
                </div>
              {:else if forms[entity.id]}
                <div class="space-y-6">
                  <SyncSettings
                    settings={forms[entity.id].syncSettings}
                    customSettings={customSyncSettings}
                    disabled={isSaving[entity.id]}
                    isSaving={isSaving[entity.id]}
                    onChange={() => save(entity, true)}
                  />

                  <div>
                    <h5 class="mb-1 border-b pb-2 text-sm font-semibold text-gray-700">Xero Connection</h5>
                    <div class="mt-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                      <p>
                        <span class="font-medium">Connected Xero organisation:</span>
                        {entity.integration.xeroTenantName}
                      </p>
                      <p class="mt-1 text-blue-800">The client controls this connection, including authentication and organisation mapping.</p>
                    </div>
                  </div>

                  <div class="space-y-4">
                    <h5 class="border-b pb-2 text-sm font-semibold text-gray-700">Posting and Budget Settings</h5>

                    <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label for="invoice-status-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Default Invoice Status</label>
                        <FieldHint text="The status applied to invoices when they are created in Xero." />
                        <select
                          id="invoice-status-{entity.id}"
                          bind:value={forms[entity.id].defaultInvoiceStatus}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        >
                          {#each postingStatuses as status}<option value={status}>{status}</option>{/each}
                        </select>
                      </div>
                      <div>
                        <label for="financial-year-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Financial Year End Month</label>
                        <FieldHint text="The month when this entity's financial year ends." />
                        <select
                          id="financial-year-{entity.id}"
                          bind:value={forms[entity.id].financialYearEndMonth}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        >
                          {#each months as month}
                            <option value={month.value}>{month.label}</option>
                          {/each}
                        </select>
                      </div>
                    </div>

                    <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label for="budget-start-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Budget Start Date</label>
                        <FieldHint text="Optional first date for budget and spend synchronisation." />
                        <input
                          id="budget-start-{entity.id}"
                          type="date"
                          bind:value={forms[entity.id].budgetStartDate}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="budget-end-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Budget End Date</label>
                        <FieldHint text="Optional last date for budget and spend synchronisation." />
                        <input
                          id="budget-end-{entity.id}"
                          type="date"
                          bind:value={forms[entity.id].budgetEndDate}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                    </div>

                    <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label for="budget-id-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Xero Budget ID</label>
                        <FieldHint text="Optional Xero budget GUID; leave blank to sync all budgets." />
                        <input
                          id="budget-id-{entity.id}"
                          type="text"
                          bind:value={forms[entity.id].xeroBudgetId}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="timezone-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Time Zone</label>
                        <FieldHint text="Used to interpret Xero dates and times for this entity." />
                        <input
                          id="timezone-{entity.id}"
                          type="text"
                          bind:value={forms[entity.id].timeZoneId}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                    </div>

                    <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label for="tracking-category-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">P&amp;L Tracking Category ID</label>
                        <FieldHint text="Optional Xero tracking category GUID used to filter P&amp;L data." />
                        <input
                          id="tracking-category-{entity.id}"
                          type="text"
                          bind:value={forms[entity.id].profitLossTrackingCategoryId}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="tracking-option-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">P&amp;L Tracking Option ID</label>
                        <FieldHint text="Optional tracking option GUID within the selected category." />
                        <input
                          id="tracking-option-{entity.id}"
                          type="text"
                          bind:value={forms[entity.id].profitLossTrackingOptionId}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                    </div>
                  </div>

                  <div class="flex justify-end">
                    <Button
                      label={isSaving[entity.id] ? 'Saving...' : 'Save'}
                      onClick={() => save(entity)}
                      disabled={!!isSaving[entity.id] || !clientKey}
                      colour="primary"
                      size="small"
                    />
                  </div>
                </div>
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>
